import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, throwError, of, firstValueFrom } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { Category, Listing } from "../data/listing";
import * as _ from "underscore";

@Injectable()
export class ListingService {
  inventoryUrl = "api/v2/inventory";
  listingsUrl = "api/v2/listings";
  categoriesUrl = "api/v2/categories";
  barcodesUrl = "api/v2/barcodes/download";
  inventoryTagsUrl = "api/v2/inventory-tags/download";
  orderSheetUrl = "api/v2/order-sheet/download";
  public observableSource = (keywords: any): Observable<Object[]> => {
    if (keywords) {
      const params = `?keywords=${encodeURIComponent(keywords)}`; // Use encodeURIComponent for safety
      return this.httpClient.get<Object[]>(this.listingsUrl + params);
    } else {
      return of([]); // Returns an empty object wrapped in an Observable
    }
  };

  constructor(private httpClient: HttpClient) {}

  async getCategories(): Promise<Category[]> {
    return await firstValueFrom(
      this.httpClient.get<Category[]>(this.categoriesUrl).pipe(
        map((response) =>
          _.map(response, (category) => ({
            id: category.id,
            title: category.title || category.name,
            isCustomCollection: category.isCustomCollection,
          }))
        )
      )
    );
  }

  async getListings(
    keywords?: string,
    categoryId?: string,
    page?: number,
    isCustomCollection?: boolean,
    nextPageUrl?: string,
    useUrl?: boolean
  ): Promise<Listing[]> {
    let params = new URLSearchParams();
    if (keywords) params.append("keywords", keywords);
    if (categoryId) params.append("category_id", categoryId);
    if (isCustomCollection)
      params.append("is_custom_collection", String(isCustomCollection));
    if (nextPageUrl)
      params.append("next_page_url", encodeURIComponent(nextPageUrl));
    if (useUrl) params.append("use_url", String(useUrl));
    return await firstValueFrom(
      this.httpClient.get<Listing[]>(
        `${this.listingsUrl}?page=${page || 1}&${params.toString()}`
      )
    );
  }

  async getInventory(
    offset?: number,
    limit?: number,
    keywords?: string
  ): Promise<any> {
    let params = new URLSearchParams();
    if (keywords) params.append("keywords", keywords);
    if (limit) params.append("limit", String(limit));
    return await firstValueFrom(
      this.httpClient.get(
        `${this.inventoryUrl}?offset=${offset || 0}&${params.toString()}`
      )
    );
  }

  async getListing(id: string): Promise<Listing> {
    const url = `${this.listingsUrl}/${id}`;
    return await firstValueFrom(this.httpClient.get<Listing>(url));
  }

  async getParent(id: string): Promise<Listing> {
    const url = `${this.listingsUrl}/${id}/parent`;
    return await firstValueFrom(this.httpClient.get<Listing>(url));
  }

  async queryMetafields(id: string): Promise<any> {
    const url = `${this.listingsUrl}/${id}/parent/metafields`;
    return await firstValueFrom(this.httpClient.get(url));
  }

  async createSamples(): Promise<Listing> {
    const url = `${this.listingsUrl}/samples`;
    return await firstValueFrom(this.httpClient.post<Listing>(url, {}));
  }

  async createBarcodes(id: string): Promise<Listing> {
    const url = `${this.listingsUrl}/${id}/parent/barcodes`;
    return await firstValueFrom(this.httpClient.post<Listing>(url, {}));
  }

  async createBulkBarcodes(ids: string[]): Promise<Listing[]> {
    const url = `${this.listingsUrl}/parent/barcodes`;
    return await firstValueFrom(this.httpClient.post<Listing[]>(url, ids));
  }

  async delete(id: string): Promise<void> {
    const url = `${this.listingsUrl}/${id}`;
    return await firstValueFrom(this.httpClient.delete<void>(url));
  }

  async deleteBulk(ids: string[]): Promise<void> {
    const url = `${this.listingsUrl}`;
    let httpOptions = {
      body: { ids: ids },
    };
    return await firstValueFrom(
      this.httpClient.request<void>("delete", url, httpOptions)
    );
  }

  async create(): Promise<Listing> {
    return await firstValueFrom(
      this.httpClient.post<Listing>(this.listingsUrl, Listing.default())
    );
  }

  async createBulk(listings: Listing[]): Promise<Listing[]> {
    return await firstValueFrom(
      this.httpClient.post<Listing[]>(this.listingsUrl, listings)
    );
  }

  async downloadBarcodes(listings: Listing[], offset: number): Promise<string> {
    return await firstValueFrom(
      this.httpClient.post<string>(
        `${this.barcodesUrl}?offset=${offset}`,
        listings
      )
    );
  }

  async downloadInventoryTags(
    listings: Listing[],
    offset: number
  ): Promise<string> {
    return await firstValueFrom(
      this.httpClient.post<string>(
        `${this.inventoryTagsUrl}?offset=${offset}`,
        listings
      )
    );
  }

  async downloadOrderSheet(listings: Listing[]): Promise<string> {
    return await firstValueFrom(
      this.httpClient.post<string>(this.orderSheetUrl, listings)
    );
  }

  async update(listing: Listing): Promise<Listing> {
    const url = `${this.listingsUrl}/${listing.id}`;
    return await firstValueFrom(this.httpClient.put<Listing>(url, listing));
  }

  async copy(listing: Listing): Promise<Listing> {
    delete listing.id; // Assuming you're creating a new listing without an ID
    listing.title += " (copy)";
    return await firstValueFrom(
      this.httpClient.post<Listing>(this.listingsUrl, listing)
    );
  }

  handleError(error: any): Promise<any> {
    console.error("An error occurred", error);
    return Promise.reject(error.message || error);
  }
}
